<script setup>
import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import ContentsRecommend01 from './recommend/ContentsRecommend01.vue';
import ContentsAnalyze01 from './analyze/ContentsAnalyze01.vue';
import ContentsNamingEnding from './ContentsNamingEnding.vue';
import { getDailyFortuneRateAPI, postDailyFortuneRateAPI } from '@/api/calendarApi';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { adaptNamingResult } from '@/utils/namingResultAdapter';
import {
  NANGMAN_NAMING_SERVICE_NAME,
  NANGMAN_NAMING_TYPE_ID,
} from '@/composables/saju-insight/useNamingPayment';
import NamingResultState from './NamingResultState.vue';
import { fastSearchByMySaju } from '@/api/Eoway';
import fiveMixin from '@/composables/fiveMixin';
import { addContentLogAPI } from '@/api/logServiceApi';

const props = defineProps({
  /** 서비스 유형 — UI 큰 틀 분기 ('recommend' | 'analyze') */
  type: { type: String, default: 'recommend' },
  /** 대상 ('person' | 'pet' | 'biz' | 'culture') — 데이터 분기 */
  target: { type: String, default: null },
  /** 세부 옵션 ('kor' | 'han' 등) — 데이터 분기 */
  detail: { type: String, default: null },
  /** PopNamingDetailInput 결과 */
  detailInput: { type: Object, default: null },
  /** 작명 기준 사주 */
  saju: { type: Object, default: null },
  /** 결제/기구매 식별자 — 컨텐츠 평가(objectKey) */
  analysisLogId: { type: [Number, String], default: null },
});

const emit = defineEmits(['goToOverView']);

// =============== 컨텐츠 평가 (낭만궁합과 동일 패턴) ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getNamingRate = async () => {
  if (!props.analysisLogId) return;
  try {
    const result = await getDailyFortuneRateAPI({
      serviceName: NANGMAN_NAMING_SERVICE_NAME,
      objectKey: `${props.analysisLogId}`,
    });
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postNamingRate = async (score) => {
  if (!props.analysisLogId) return;
  try {
    const result = await postDailyFortuneRateAPI({
      score,
      serviceName: NANGMAN_NAMING_SERVICE_NAME,
      objectKey: `${props.analysisLogId}`,
    });
    if (result) {
      ratingStatus.value = true;
      getNamingRate();
    }
  } catch (e) {}
};

// =============== 결과 데이터 (GET /fast/base-service, analysisLogId 기반) ===============
const resultLoading = ref(false);
const resultError = ref('');
const recommendList = ref([]);
const criteriaRules = ref([]);
const criteriaTailRows = ref([]);
const analysis = ref(null);
const yongsinOhaeng = ref([]);

// 내 사주 천간지지 4기둥 — 응답 사주정보.명식 우선, 없으면 fastSearch birthganji 폴백
const fm = fiveMixin();
const sajuPillars = ref([]);
const sajuTags = ref([]);
const PILLAR_POS = [['hour', '시'], ['day', '일'], ['month', '월'], ['year', '년']];

/** 한글 간지 한 쌍(천간/지지) → 팝업 pillar(한자·오행색 포함) */
const pillarFromGanji = (label, sky, earth) => {
  const skyFive = fm.ganjiJsonData.ganji[sky]?.five;
  const earthFive = fm.ganjiJsonData.ganji[earth]?.five;
  return {
    label,
    stem: fm.getHanjaTxt(sky, 'sky') || sky,
    branch: fm.getHanjaTxt(earth, 'earth') || earth,
    kor: `${sky}${earth}`,
    stemColor: fm.ganjiJsonData.five[skyFive]?.color || '#404040',
    branchColor: fm.ganjiJsonData.five[earthFive]?.color || '#404040',
  };
};
const buildSajuPillars = (birthganji) => {
  if (!birthganji?.sky || !birthganji?.earth) return [];
  return PILLAR_POS.map(([pos, label]) =>
    pillarFromGanji(label, birthganji.sky[pos]?.value || '', birthganji.earth[pos]?.value || ''));
};
/** 어댑터 sajuInfo.myeongsik([{label, ganji:'임술'}]) → pillars (간지 문자열을 천간+지지로 분리) */
const buildPillarsFromMyeongsik = (myeongsik) => {
  if (!Array.isArray(myeongsik) || !myeongsik.length) return [];
  return myeongsik.map(({ label, ganji }) =>
    pillarFromGanji(label, ganji?.[0] || '', ganji?.[1] || ''));
};
/**
 * 결과 응답 사주정보 적용 — tags 세팅 + 명식이 있으면 pillars를 명식 기준으로 채운다.
 * 명식이 없으면 false를 반환해 호출부가 fastSearch 폴백을 타도록 한다.
 */
const applySajuInfo = (info) => {
  sajuTags.value = info?.tags || [];
  const pillars = buildPillarsFromMyeongsik(info?.myeongsik);
  if (pillars.length) {
    sajuPillars.value = pillars;
    return true;
  }
  return false;
};
/** 요청 세대 — 추천 모드는 최대 ~130초 걸리므로, 로딩 중 우측 패널에서 다른 구매내역을
 *  클릭하면 먼저 떠난 이전 응답이 늦게 도착해 새 결과를 덮어쓸 수 있다. 마지막 요청만 반영한다. */
let fetchSeq = 0;
onBeforeUnmount(() => { fetchSeq += 1; });

const fetchSajuPillars = async (seq) => {
  const gid = props.saju?.guestUserId;
  if (!gid) { if (seq === fetchSeq) sajuPillars.value = []; return; }
  try {
    const data = await fastSearchByMySaju(gid, props.saju?.guestUserName || props.saju?.name || '');
    if (seq !== fetchSeq) return;
    sajuPillars.value = buildSajuPillars(data?.birthganji);
  } catch (e) {
    if (seq === fetchSeq) sajuPillars.value = [];
  }
};

const fetchResult = async () => {
  if (!props.analysisLogId) return;
  const seq = ++fetchSeq;
  resultLoading.value = true;
  resultError.value = '';
  recommendList.value = [];
  criteriaRules.value = [];
  criteriaTailRows.value = [];
  analysis.value = null;
  sajuPillars.value = [];
  sajuTags.value = [];
  try {
    const res = await fetchBaseServiceAPI(
      { analysisTypeId: NANGMAN_NAMING_TYPE_ID, analysisLogId: props.analysisLogId },
      { timeout: 200_000 }, // 추천 모드 LLM 동기 호출 ~130초 대비
    );
    if (seq !== fetchSeq) return; // 이후 요청이 시작됨 — stale 응답 폐기
    const { success, data } = res || {};
    if (success === false) {
      resultError.value = data?.message || '결과를 불러오지 못했어요.';
      return;
    }
    const adapted = adaptNamingResult(data);
    if (adapted.kind === 'error') {
      resultError.value = adapted.message;
    } else if (adapted.kind === 'recommend') {
      recommendList.value = adapted.list;
      criteriaRules.value = adapted.criteriaRules || [];
      criteriaTailRows.value = adapted.criteriaTailRows || [];
      yongsinOhaeng.value = adapted.yongsinOhaeng;
      if (!applySajuInfo(adapted.sajuInfo)) await fetchSajuPillars(seq);
    } else if (adapted.kind === 'analyze') {
      // 어댑터는 응답에서 추출한 이름을 analysis 밖(adapted.name)에 반환 — 병합해 보존.
      // detailInput이 없는 옛 구매 건 재열람 시 이름 카드 폴백(analysis.name)으로 쓰인다.
      analysis.value = adapted.analysis
        ? { ...adapted.analysis, name: adapted.name || '' }
        : adapted.analysis;
      yongsinOhaeng.value = adapted.yongsinOhaeng;
      if (!applySajuInfo(adapted.sajuInfo)) await fetchSajuPillars(seq);
    }
  } catch (e) {
    if (seq !== fetchSeq) return;
    resultError.value =
      e?.response?.status === 408
        ? '결과 생성이 지연되고 있어요. 잠시 후 다시 시도해주세요.'
        : '결과를 불러오지 못했어요.';
  } finally {
    if (seq === fetchSeq) resultLoading.value = false;
  }
};

onMounted(() => {
  getNamingRate();
  fetchResult();
});

/** 다른 기구매 항목 선택 등으로 대상이 바뀌면 상태 리셋 후 재조회 */
watch(
  () => props.analysisLogId,
  () => {
    ratingResult.value = 0;
    ratingStatus.value = false;
    indexNo.value = 1; // 마무리 페이지를 보던 중 다른 항목을 선택해도 첫 페이지(결과)부터 표시
    getNamingRate();
    fetchResult();
  },
);

/** 서비스 유형별 페이지 목차 */
const CONTENTS_BY_TYPE = {
  recommend: [
    { id: 1, title: '1. 추천 이름' },
    { id: 2, title: '2. 마무리' },
  ],
  analyze: [
    { id: 1, title: '1. 분석 결과' },
    { id: 2, title: '2. 마무리' },
  ],
};

const contents = computed(() => CONTENTS_BY_TYPE[props.type] || []);
const indexNo = ref(1);
const totalPages = computed(() => contents.value.length);

const moveToPage = (id) => {
  indexNo.value = id;
};

const updateIndexNo = (id) => {
  addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: String(id) });
  moveToPage(id);
};

const goToOverView = () => {
  addContentLogAPI({ contentType: 'ap_naming_btn_restart' });
  emit('goToOverView');
};

const openCriteria = () => {
  // TODO: 추천 기준 보기 팝업 연결
};

const selectName = (item) => {
  // TODO: 좌측에서 추천 카드 클릭 시 우측 detail 데이터 교체
};

/** 타입 변경 시 첫 페이지로 리셋 */
watch(() => props.type, () => {
  indexNo.value = 1;
});
</script>

<template>
  <div class="full-height naming-detail-wrap">
    <!-- 이름 추천 -->
    <template v-if="type === 'recommend'">
      <template v-if="indexNo === 1">
        <NamingResultState
          v-if="resultLoading || resultError"
          class="full-height"
          :loading="resultLoading"
          :error="resultError"
          :long="true"
          @retry="fetchResult"
        />
        <ContentsRecommend01
          v-else
          class="full-height"
          :target="target"
          :detail="detail"
          :detail-input="detailInput"
          :saju="saju"
          :recommend-list="recommendList"
          :criteria-rules="criteriaRules"
          :criteria-tail-rows="criteriaTailRows"
          :saju-pillars="sajuPillars"
          :saju-tags="sajuTags"
          @open-criteria="openCriteria"
          @select-name="selectName"
        />
      </template>
      <ContentsNamingEnding
        v-else-if="indexNo === 2"
        class="full-height"
        type="recommend"
        :rating-result="ratingResult"
        :rating-status="ratingStatus"
        @post-rate="postNamingRate"
        @go-to-over-view="goToOverView"
      />
    </template>

    <!-- 기존 이름 분석 -->
    <template v-else-if="type === 'analyze'">
      <template v-if="indexNo === 1">
        <NamingResultState
          v-if="resultLoading || resultError"
          class="full-height"
          :loading="resultLoading"
          :error="resultError"
          :long="false"
          @retry="fetchResult"
        />
        <ContentsAnalyze01
          v-else-if="analysis"
          class="full-height"
          :target="target"
          :detail="detail"
          :detail-input="detailInput"
          :saju="saju"
          :analysis="analysis"
          :saju-pillars="sajuPillars"
          :saju-tags="sajuTags"
          @open-criteria="openCriteria"
        />
      </template>
      <ContentsNamingEnding
        v-else-if="indexNo === 2"
        class="full-height"
        type="analyze"
        :rating-result="ratingResult"
        :rating-status="ratingStatus"
        @post-rate="postNamingRate"
        @go-to-over-view="goToOverView"
      />
    </template>

    <div class="contentsListBtnBox">
      <ContentsList
        :items="contents"
        :cur-page="indexNo"
        :last-page="totalPages"
        :hide-toc="true"
        :disable-prev="true"
        :loop-to-first="true"
        @update-index-no="updateIndexNo"
        @go-to-over-view="goToOverView"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.naming-detail-wrap {
  display: flex;
  flex-direction: column;
}
</style>
